import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Brain, Trophy, Calendar, Users, Award, CheckCircle, Zap, Target, 
  Sparkles, Clock, MapPin, ArrowRight, Star, ChevronRight, Cpu,
  TrendingUp, Globe, Shield, Rocket, Play
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const IAPLPage = () => {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await axios.get(`${API}/iapl/config`);
        setConfig(response.data);
      } catch (err) {
        console.error('Failed to fetch IAPL config:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();

    // Countdown to finals
    const eventDate = new Date('2026-03-29T09:00:00');
    const timer = setInterval(() => {
      const now = new Date();
      const diff = eventDate - now;
      if (diff > 0) {
        setTimeLeft({
          days: Math.floor(diff / (1000 * 60 * 60 * 24)),
          hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((diff / (1000 * 60)) % 60),
          seconds: Math.floor((diff / 1000) % 60)
        });
      }
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const aiAreas = [
    { icon: Brain, name: 'Machine Learning', description: 'Deep learning, neural networks, model optimization' },
    { icon: Cpu, name: 'Computer Vision', description: 'Image recognition, object detection, video analysis' },
    { icon: Globe, name: 'Natural Language', description: 'NLP, chatbots, language models, text generation' },
    { icon: Zap, name: 'Generative AI', description: 'Image generation, creative AI, multimodal systems' },
    { icon: Shield, name: 'AI for Healthcare', description: 'Medical diagnosis, drug discovery, health analytics' },
    { icon: TrendingUp, name: 'AI for Finance', description: 'Trading algorithms, fraud detection, risk analysis' }
  ];

  const timeline = [
    { phase: 'Registration', date: 'Feb - Mar 2026', status: 'active', icon: Users },
    { phase: 'Online Training', date: '15-20 Mar 2026', status: 'upcoming', icon: Play },
    { phase: 'Product Development', date: '20-23 Mar 2026', status: 'upcoming', icon: Cpu },
    { phase: 'Grand Finals', date: '12 Apr 2026', status: 'upcoming', icon: Trophy }
  ];

  const benefits = [
    'Travel expenses covered for finalists',
    'Stay and food for finals included',
    'Certificate of participation',
    'Networking with industry experts',
    'Internship opportunities',
    'Mentorship from AI professionals'
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-purple-500/30 border-t-purple-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  // Coming Soon State
  if (!config?.enabled) {
    return (
      <div className="min-h-screen bg-[#050505] text-white overflow-hidden">
        {/* Animated Background */}
        <div className="fixed inset-0 z-0">
          <div className="absolute inset-0 bg-[linear-gradient(rgba(112,0,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(112,0,255,0.03)_1px,transparent_1px)] bg-[size:100px_100px]"></div>
          <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-purple-500/20 rounded-full blur-[150px] animate-pulse"></div>
          <div className="absolute bottom-1/4 right-1/4 w-[600px] h-[600px] bg-pink-500/20 rounded-full blur-[150px] animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>

        <section className="relative min-h-screen flex items-center justify-center px-6">
          <div className="relative z-10 text-center max-w-4xl">
            <div className="inline-flex items-center gap-3 bg-purple-500/20 border border-purple-500/30 rounded-full px-6 py-3 mb-8 backdrop-blur-sm">
              <Brain className="text-purple-400" size={24} />
              <span className="text-purple-400 font-bold tracking-wide">IAPL 2026</span>
            </div>

            <h1 className="text-5xl md:text-7xl lg:text-8xl font-black tracking-tighter mb-6">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-400 via-pink-500 to-purple-400">
                AI LEAGUE
              </span>
            </h1>

            <p className="text-2xl md:text-3xl text-gray-400 mb-8">
              India's Premier AI Competition
            </p>

            <div className="inline-flex items-center gap-4 bg-black/40 backdrop-blur-xl border border-white/10 rounded-2xl px-8 py-4 mb-12">
              <Clock className="text-purple-400" size={28} />
              <div className="text-left">
                <p className="text-gray-400 text-sm">Status</p>
                <p className="text-2xl font-black text-white">Coming Soon</p>
              </div>
            </div>

            <p className="text-gray-500 text-lg max-w-2xl mx-auto mb-8">
              Get ready for India's most prestigious AI competition. 
              Build innovative AI solutions, compete with the best minds, and win big!
            </p>

            <button
              onClick={() => navigate('/register')}
              className="px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-600 rounded-full font-bold text-lg hover:shadow-[0_0_40px_rgba(168,85,247,0.4)] transition-all"
            >
              Get Notified When Registration Opens
            </button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white overflow-hidden">
      {/* Animated Background */}
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(112,0,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(112,0,255,0.03)_1px,transparent_1px)] bg-[size:100px_100px]"></div>
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-purple-500/15 rounded-full blur-[150px] animate-pulse"></div>
        <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-pink-500/15 rounded-full blur-[150px] animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-blue-500/5 rounded-full blur-[200px]"></div>
      </div>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center px-6 md:px-12 lg:px-24 pt-20">
        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div>
              {/* Badge */}
              <div className="inline-flex items-center gap-3 bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-full px-5 py-2 mb-8 backdrop-blur-sm">
                <Brain className="text-purple-400" size={18} />
                <span className="text-purple-400 font-semibold text-sm tracking-wide">IAPL 2026 • IIT GANDHINAGAR</span>
                <span className="bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs px-3 py-1 rounded-full font-bold animate-pulse shadow-[0_0_15px_rgba(34,197,94,0.5)]">LIVE</span>
              </div>

              {/* Title */}
              <h1 className="font-black tracking-tighter mb-6">
                <span className="block text-5xl md:text-7xl lg:text-8xl bg-clip-text text-transparent bg-gradient-to-r from-purple-400 via-pink-500 to-purple-400">
                  AI LEAGUE
                </span>
                <span className="block text-3xl md:text-4xl lg:text-5xl mt-3 text-gray-400">
                  India's <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-pink-500">Premier</span> AI Competition
                </span>
              </h1>

              {/* Description */}
              <p className="text-lg md:text-xl text-gray-400 max-w-xl mb-8 leading-relaxed">
                Build innovative AI solutions, compete with India's brightest minds, 
                and win from a prize pool of
                <span className="text-purple-400 font-bold"> ₹5,00,000</span>.
              </p>

              {/* Prize Highlight */}
              <div className="inline-flex items-center gap-4 bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border border-yellow-500/30 rounded-2xl px-6 py-4 mb-8">
                <Trophy className="text-yellow-400" size={36} />
                <div>
                  <p className="text-sm text-gray-400">Total Prize Pool</p>
                  <p className="text-3xl font-black bg-clip-text text-transparent bg-gradient-to-r from-yellow-400 to-orange-500">₹5,00,000</p>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => navigate('/register')}
                  className="group relative px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-600 rounded-full font-bold text-lg overflow-hidden transition-all hover:scale-105 hover:shadow-[0_0_40px_rgba(168,85,247,0.4)]"
                  data-testid="iapl-register-btn"
                >
                  <span className="relative z-10 flex items-center justify-center gap-2">
                    Register Now
                    <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                  </span>
                </button>
                <button
                  onClick={() => document.getElementById('timeline').scrollIntoView({ behavior: 'smooth' })}
                  className="px-8 py-4 bg-white/5 border border-white/20 rounded-full font-bold text-lg hover:bg-white/10 hover:border-white/30 transition-all backdrop-blur-sm"
                >
                  View Timeline
                </button>
              </div>
            </div>

            {/* Right - Countdown & Stats */}
            <div className="space-y-6">
              {/* Countdown */}
              <div className="bg-black/40 backdrop-blur-xl border border-purple-500/20 rounded-3xl p-8">
                <div className="flex items-center gap-2 mb-6">
                  <Zap className="text-purple-400" size={20} />
                  <span className="text-gray-400 font-semibold">Finals Begin In</span>
                </div>
                <div className="grid grid-cols-4 gap-4">
                  {[
                    { label: 'DAYS', value: timeLeft.days },
                    { label: 'HOURS', value: timeLeft.hours },
                    { label: 'MINS', value: timeLeft.minutes },
                    { label: 'SECS', value: timeLeft.seconds }
                  ].map((item, idx) => (
                    <div key={idx} className="text-center">
                      <div className="bg-gradient-to-b from-purple-500/20 to-purple-500/5 border border-purple-500/20 rounded-xl p-4">
                        <p className="text-3xl md:text-4xl font-black text-white">{String(item.value).padStart(2, '0')}</p>
                      </div>
                      <p className="text-[10px] text-gray-500 font-semibold tracking-widest mt-2">{item.label}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-black/40 backdrop-blur-md border border-white/10 rounded-2xl p-6 text-center">
                  <p className="text-3xl font-black text-purple-400">₹3,999</p>
                  <p className="text-sm text-gray-400">Per Participant</p>
                </div>
                <div className="bg-black/40 backdrop-blur-md border border-white/10 rounded-2xl p-6 text-center">
                  <p className="text-3xl font-black text-pink-400">2-5</p>
                  <p className="text-sm text-gray-400">Team Members</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-purple-500/30 rounded-full flex items-start justify-center p-2">
            <div className="w-1.5 h-3 bg-purple-400 rounded-full animate-pulse"></div>
          </div>
        </div>
      </section>

      {/* AI Focus Areas */}
      <section className="relative py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 text-purple-400 font-semibold mb-4">
              <div className="w-8 h-px bg-purple-400"></div>
              AI DOMAINS
              <div className="w-8 h-px bg-purple-400"></div>
            </div>
            <h2 className="text-4xl md:text-6xl font-black tracking-tight mb-4">
              Choose Your <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-pink-500">Specialization</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Pick your AI domain and build something extraordinary
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {aiAreas.map((area, idx) => {
              const Icon = area.icon;
              return (
                <div
                  key={idx}
                  className="group relative bg-[#0a0a0a] border border-white/10 rounded-2xl p-8 transition-all duration-300 hover:border-purple-500/50 hover:shadow-[0_0_40px_rgba(168,85,247,0.15)]"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-pink-500/5 opacity-0 group-hover:opacity-100 rounded-2xl transition-opacity"></div>
                  <div className="relative">
                    <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                      <Icon className="text-white" size={28} />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">{area.name}</h3>
                    <p className="text-gray-400 text-sm">{area.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section id="timeline" className="relative py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#0a0a0a]">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 text-pink-400 font-semibold mb-4">
              <div className="w-8 h-px bg-pink-400"></div>
              JOURNEY
              <div className="w-8 h-px bg-pink-400"></div>
            </div>
            <h2 className="text-4xl md:text-6xl font-black tracking-tight mb-4">
              Event <span className="bg-clip-text text-transparent bg-gradient-to-r from-pink-400 to-purple-500">Timeline</span>
            </h2>
          </div>

          <div className="relative">
            {/* Vertical Line */}
            <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-purple-500 via-pink-500 to-purple-500"></div>

            {timeline.map((item, idx) => {
              const Icon = item.icon;
              const isLeft = idx % 2 === 0;
              return (
                <div key={idx} className={`relative flex items-center gap-8 mb-12 ${isLeft ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                  {/* Timeline Node */}
                  <div className={`absolute left-8 md:left-1/2 -translate-x-1/2 w-4 h-4 rounded-full ${item.status === 'active' ? 'bg-purple-500 shadow-[0_0_20px_rgba(168,85,247,0.8)]' : 'bg-[#0a0a0a] border-2 border-purple-500/50'}`}></div>

                  {/* Content Card */}
                  <div className={`ml-20 md:ml-0 md:w-[calc(50%-40px)] ${isLeft ? 'md:pr-12 md:text-right' : 'md:pl-12'}`}>
                    <div className={`group bg-black/40 backdrop-blur-md border ${item.status === 'active' ? 'border-purple-500/50 shadow-[0_0_30px_rgba(168,85,247,0.15)]' : 'border-white/10'} rounded-2xl p-6 hover:border-purple-500/50 transition-all`}>
                      <div className={`flex items-center gap-4 mb-3 ${isLeft ? 'md:flex-row-reverse' : ''}`}>
                        <div className={`w-12 h-12 ${item.status === 'active' ? 'bg-gradient-to-br from-purple-500 to-pink-600' : 'bg-white/10'} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform`}>
                          <Icon className="text-white" size={24} />
                        </div>
                        <div className={isLeft ? 'md:text-right' : ''}>
                          <p className="text-purple-400 font-mono text-sm">{item.date}</p>
                          <h3 className="text-xl font-bold text-white">{item.phase}</h3>
                        </div>
                      </div>
                      {item.status === 'active' && (
                        <span className="inline-flex items-center gap-1 px-3 py-1 bg-purple-500/20 border border-purple-500/30 rounded-full text-xs text-purple-400 font-semibold">
                          <span className="w-2 h-2 bg-purple-400 rounded-full animate-pulse"></span>
                          Active Now
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="relative py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-purple-400 font-semibold mb-4">
                <div className="w-8 h-px bg-purple-400"></div>
                PERKS
                <div className="w-8 h-px bg-purple-400"></div>
              </div>
              <h2 className="text-4xl md:text-5xl font-black tracking-tight mb-6">
                What You <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-pink-500">Get</span>
              </h2>
              <p className="text-gray-400 text-lg mb-8">
                Beyond the competition, IAPL offers an unparalleled experience for AI enthusiasts.
              </p>

              <div className="space-y-4">
                {benefits.map((benefit, idx) => (
                  <div key={idx} className="flex items-center gap-4 p-4 bg-black/40 backdrop-blur-md border border-white/10 rounded-xl hover:border-purple-500/30 transition-all">
                    <CheckCircle className="text-purple-400 flex-shrink-0" size={24} />
                    <span className="text-white">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-3xl blur-2xl"></div>
              <div className="relative bg-black/40 backdrop-blur-md border border-purple-500/20 rounded-3xl p-10 text-center">
                <div className="w-24 h-24 bg-gradient-to-br from-purple-500 to-pink-600 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-[0_0_40px_rgba(168,85,247,0.4)]">
                  <Trophy className="text-white" size={48} />
                </div>
                <p className="text-5xl font-black bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-pink-500 mb-2">₹5,00,000</p>
                <p className="text-gray-400 text-lg mb-6">Total Prize Pool</p>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-yellow-400">₹2,00,000</p>
                    <p className="text-xs text-gray-500">1st Prize</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-300">₹1,50,000</p>
                    <p className="text-xs text-gray-500">2nd Prize</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-amber-700">₹1,00,000</p>
                    <p className="text-xs text-gray-500">3rd Prize</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative py-32 px-6 md:px-12 lg:px-24 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 via-pink-500/20 to-purple-500/20"></div>
          <div className="absolute inset-0 bg-[#050505]/80"></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-6 py-2 mb-8">
            <Brain className="text-purple-400" size={18} />
            <span className="text-white font-semibold">Join the AI Revolution</span>
          </div>

          <h2 className="text-4xl md:text-6xl font-black tracking-tight mb-6">
            Ready to Build the <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-400 via-pink-500 to-purple-400">Future of AI</span>?
          </h2>
          <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto">
            Register your team now and compete for glory in India's most prestigious AI competition.
          </p>

          <button
            onClick={() => navigate('/register')}
            className="group relative px-12 py-5 bg-gradient-to-r from-purple-500 via-pink-500 to-purple-500 rounded-full font-bold text-xl overflow-hidden transition-all hover:scale-105 hover:shadow-[0_0_60px_rgba(168,85,247,0.4)]"
            data-testid="iapl-final-register-btn"
          >
            <span className="relative z-10 flex items-center justify-center gap-2">
              Register Your Team
              <ArrowRight size={24} className="group-hover:translate-x-1 transition-transform" />
            </span>
          </button>

          <div className="mt-12 flex justify-center gap-8">
            <div className="flex items-center gap-2 text-gray-400">
              <MapPin size={18} className="text-purple-400" />
              <span>IIT Gandhinagar</span>
            </div>
            <div className="flex items-center gap-2 text-gray-400">
              <Calendar size={18} className="text-pink-400" />
              <span>March 2026</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default IAPLPage;
